/**
 * @file Feature-flag visibility rules for sidebar dropdown options.
 *
 * Kept in the shared sider utilities so the visibility rules remain
 * independently testable and reusable by sidebar consumers.
 *
 * Companion to `filterSidebarOptions` in `sidebarAccess.js`, which covers the
 * permission / whitelist / development_mode / product axes. This module covers
 * only the feature-flag axis.
 *
 * See docs/components/sider/sidebar-entries-visibility.md.
 */
import { checkPamFeature } from "../../hooks/useFeatureFlag";

/**
 * Drops options whose feature flags are off.
 *
 * - `forceVisible` short-circuits every check below.
 * - `featureFlag` accepts an array when a route needs several flags at once;
 *   the check is AND (every flag must be on).
 * - `hideWhenFeatureFlag` is the inverse: present and on → hidden.
 * - `innerFeatureFlags` is OR (any one on is enough).
 * - Options with `children` are recursed into and kept when at least one child
 *   survives, or when the option itself is navigable.
 *
 * @param {Array<Object>} [options]
 * @returns {Array<Object>}
 */
export const filterVisibleOptions = (options = []) =>
  options.reduce((visibleOptions, option) => {
    if (option.forceVisible) {
      visibleOptions.push(option);
      return visibleOptions;
    }

    if (option.featureFlag) {
      const requiredFlags = Array.isArray(option.featureFlag)
        ? option.featureFlag
        : [option.featureFlag];
      if (!requiredFlags.every((flag) => checkPamFeature(flag))) {
        return visibleOptions;
      }
    }

    if (
      option.hideWhenFeatureFlag &&
      checkPamFeature(option.hideWhenFeatureFlag)
    ) {
      return visibleOptions;
    }

    if (option.innerFeatureFlags?.length) {
      const hasEnabledInnerFlag = option.innerFeatureFlags.some((flag) =>
        checkPamFeature(flag),
      );
      if (!hasEnabledInnerFlag) {
        return visibleOptions;
      }
    }

    if (Array.isArray(option.children)) {
      if (option.children.length === 0) {
        visibleOptions.push(option);
        return visibleOptions;
      }

      const visibleChildren = filterVisibleOptions(option.children);
      if (visibleChildren.length > 0 || option.navigate) {
        visibleOptions.push({
          ...option,
          children: visibleChildren,
        });
      }
      return visibleOptions;
    }

    visibleOptions.push(option);
    return visibleOptions;
  }, []);

export default filterVisibleOptions;
